'use client'

import type { ReactNode } from 'react'

/**
 * Blocos de formulário compartilhados entre as telas de cadastro.
 *
 * Existem para acabar com dois vícios que se repetiam página a página: label
 * colado no input (cada campo inventava o próprio espaçamento) e cartão sem
 * hierarquia (título solto dentro do mesmo fundo do conteúdo).
 */

export interface FormFieldProps {
    label: string
    /** id do controle — sem isso o clique no rótulo não foca o campo. */
    htmlFor?: string
    icon?: ReactNode
    required?: boolean
    optional?: boolean
    /** Texto de apoio abaixo do controle. */
    hint?: ReactNode
    children: ReactNode
    className?: string
}

export function FormField({
    label,
    htmlFor,
    icon,
    required,
    optional,
    hint,
    children,
    className = '',
}: FormFieldProps) {
    return (
        <div className={`flex flex-col gap-2 ${className}`}>
            <label
                htmlFor={htmlFor}
                className="flex items-center gap-1.5 text-sm font-medium text-base-content"
            >
                {icon && (
                    <span className="flex items-center text-base-content/40">
                        {icon}
                    </span>
                )}
                {label}
                {required && <span className="text-error">*</span>}
                {optional && (
                    <span className="text-xs font-normal text-base-content/40">
                        (opcional)
                    </span>
                )}
            </label>
            {children}
            {hint && (
                <p className="text-xs leading-relaxed text-base-content/50">
                    {hint}
                </p>
            )}
        </div>
    )
}

export interface FormSectionProps {
    icon: ReactNode
    title: string
    subtitle?: string
    /** Âncora usada pelo índice lateral das telas longas. */
    id?: string
    /** Conteúdo alinhado à direita do cabeçalho (toggles, ações da seção). */
    actions?: ReactNode
    children: ReactNode
    className?: string
    /** Espaçamento vertical entre os filhos do corpo. */
    bodyClassName?: string
}

export function FormSection({
    icon,
    title,
    subtitle,
    id,
    actions,
    children,
    className = '',
    bodyClassName = 'flex flex-col gap-6 p-5',
}: FormSectionProps) {
    return (
        /* Sem `overflow-hidden`: quase toda seção tem um selector com dropdown
           `absolute`, e o clip cortava a lista na borda do card. O canto
           arredondado do cabeçalho é resolvido nele mesmo (`rounded-t-2xl`),
           que era o único motivo do clip existir. */
        <section
            id={id}
            className={`scroll-mt-28 rounded-2xl border border-base-300 bg-base-100 ${className}`}
        >
            <header className="flex flex-wrap items-center gap-3 rounded-t-2xl border-b border-base-300 bg-base-200/40 px-5 py-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-base-100 text-secondary ring-1 ring-base-300">
                    {icon}
                </span>
                <div className="min-w-0 flex-1">
                    <h2 className="text-sm font-semibold text-base-content">
                        {title}
                    </h2>
                    {subtitle && (
                        <p className="text-xs text-base-content/50">
                            {subtitle}
                        </p>
                    )}
                </div>
                {actions}
            </header>
            <div className={bodyClassName}>{children}</div>
        </section>
    )
}
